import {
  ActionButton,
  Paginator,
  Show,
  TableComponent,
  TableLoader,
} from "components";
import { FC, useMemo } from "react";
import {
  useAppLocation,
  useAppNavigate,
  useAppSearch,
} from "_graphql/cache/auth";

import { Action } from "components/buttons/action-button";
import FilterMenu from "./filter";
import _ from "lodash";
import clsx from "clsx";
import moment from "moment";
import numeral from "numeral";
import { useKEKOffers } from "hooks/data/use-offers";
import { usePagination } from "hooks";

type Props = {};

const InsurerOffers: FC<Props> = () => {
  const navigate = useAppNavigate();
  const searchParams = useAppSearch();
  const { params } = useAppLocation();
  const { offset, limit, setPage } = usePagination();

  const filter = useMemo(
    () => ({
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 10,
      search: (searchParams.search || "").toString(),
      args: {
        insurer_id: params?.insurer || undefined,
        approval_status: searchParams?.approval_status,
        expiry_status: searchParams?.expiry_status,
        class_of_business_id: searchParams?.business,
        staff_id: searchParams?.employee,
        status: ["PENDING", "OPEN", "CLOSED"],
        claim_status: [],
        payment_status: searchParams?.payment_status || [],
        year: searchParams.year?.toString() || moment().format("YYYY"),
        search: (searchParams.search || "").toString(),
        limit,
        offset,
      },
    }),
    [searchParams, params?.insurer]
  );
  const { loading, offers, refetch, count } = useKEKOffers(filter as any);
  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  const expandItem = (id: string) => {
    navigate({
      to: `./${id}/payments`,
    });
  };
  return (
    <div className="flex flex-1 overflow-y-auto">
      <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
        <TableComponent
          title={"offers"}
          hasSearch
          loading={loading}
          pagination={() => (
            <Paginator
              offset={offset}
              limit={limit}
              totalItems={count || 0}
              currentSize={offers.length}
              setOffset={setPage}
            />
          )}
          columns={[
            {
              name: "Policy #",
              accessor: "offer_detail.policy_number",
              render: (value) => (
                <div className="flex flex-col items-start flex-wrap break-all break-words">
                  <span className="font-bold text-xs overflow-hidden text-ellipsis ">
                    {value.offer_detail?.policy_number || "N/A"}
                  </span>
                  <div
                    className={clsx(
                      "p-0.5 rounded-full",
                      {
                        " text-green-600": value.offer_from === "INTERNAL",
                        " text-yellow-600": value.offer_from !== "INTERNAL",
                      },
                      "text-xs font-medium"
                    )}
                  >
                    <span>{value.offer_from}</span>
                  </div>
                </div>
              ),
            },
            {
              name: "Class of Business",
              accessor: "classofbusiness.business_name",
              render: (value) => (
                <div className="flex flex-col text-xs">
                  <span className="font-medium">
                    {value.classofbusiness?.business_name || "N/A"}
                  </span>
                </div>
              ),
            },
            {
              name: "Insured",
              accessor: "offer_detail.insured_by",
              render: (value) => {
                const details = JSON.parse(
                  value?.offer_detail?.offer_details || "[]"
                );
                return (
                  <div className="flex flex-col text-xs ">
                    <span className="font-medium">
                      {value.offer_detail?.insured_by || "N/A"}
                    </span>
                    <span className="font-light">
                      {[
                        "Motor Comprehensive Fleet",
                        "Motor Comprehensive",
                        "Motor Comprehensive (Automobile Fac Facility)",
                      ].includes(value?.classofbusiness?.business_name || "")
                        ? details?.find(
                            (el: { [x: string]: string }) =>
                              el["keydetail"] === "Vehicle Reg No" ||
                              el["keydetail"] === "Vehicle Reg No."
                          )?.value
                        : ""}
                    </span>
                  </div>
                );
              },
            },
            {
              name: "Expected premium",
              accessor: "created_at",
              render: (value) => {
                const hasEndorsement = _.last(value?.offer_endorsements);

                const endorsementAddon = hasEndorsement
                  ? numeral(hasEndorsement?.fac_premium)
                      .subtract(hasEndorsement?.commission_amount || 0)
                      .value() || 0
                  : 0;

                const expected = hasEndorsement
                  ? endorsementAddon
                  : numeral(value?.fac_premium)
                      .subtract(value?.commission_amount || 0)
                      .add(endorsementAddon)
                      .value() || 0;
                return <span>{numeral(expected).format("0,0.00")}</span>;
              },
            },
            {
              name: "Outstanding premium",
              accessor: "created_at",
              render: (value) => {
                const hasEndorsement = _.last(value?.offer_endorsements);

                const endorsementAddon = hasEndorsement
                  ? numeral(hasEndorsement?.fac_premium)
                      .subtract(hasEndorsement?.commission_amount || 0)
                      .value() || 0
                  : 0;

                const expected = hasEndorsement
                  ? endorsementAddon
                  : numeral(value?.fac_premium)
                      .subtract(value?.commission_amount || 0)
                      .add(endorsementAddon)
                      .value() || 0;

                const paymentsMade = numeral(
                  value?.offer_payment?.reduce(
                    (sum: number, payment: any) => sum + payment.payment_amount,
                    0
                  )
                ).value();
                const outstanding = numeral(expected)
                  .subtract(paymentsMade)
                  .value();
                return <span>{numeral(outstanding).format("0,0.00")}</span>;
              },
            },
            {
              name: "Sum Insured",
              accessor: "sum_insured",
              render: (value) => (
                <span>
                  {value?.offer_detail?.currency}{" "}
                  {numeral(value?.sum_insured).format("0,0.00")}
                </span>
              ),
            },
            {
              name: "Distribution Status",
              accessor: "distribute_status",
              render: (value) => (
                <span>{value?.distribute_status || "N/A"}</span>
              ),
            },
            {
              name: "Offer Status",
              accessor: "offer_status",
              render: ({ offer_status }) => (
                <div
                  className={clsx(
                    "w-auto flex items-center justify-center p-0.5 rounded-full",
                    {
                      "bg-yellow-100": offer_status === "PENDING",
                      "bg-blue-100": offer_status === "OPEN",
                      "bg-green-100": offer_status === "CLOSED",
                    }
                  )}
                >
                  <span
                    className={clsx("text-xs font-bold", {
                      "text-yellow-600": offer_status === "PENDING",
                      "text-blue-600": offer_status === "OPEN",
                      "text-green-600": offer_status === "CLOSED",
                    })}
                  >
                    {_.startCase(offer_status?.toLowerCase())}
                  </span>
                </div>
              ),
            },
            {
              name: "Payment Status",
              accessor: "payment_status",
              render: ({ payment_status }) => (
                <div>
                  <div
                    className={clsx(
                      "w-auto flex items-center justify-center p-0.5 rounded-full",
                      {
                        "bg-red-100": payment_status === "UNPAID",
                        "bg-teal-100": payment_status === "PAID",
                        "bg-sky-100": payment_status === "PARTPAYMENT",
                      }
                    )}
                  >
                    <span
                      className={clsx("text-xs font-bold", {
                        "text-red-600": payment_status === "UNPAID",
                        "text-teal-600": payment_status === "PAID",
                        "text-sky-600": payment_status === "PARTPAYMENT",
                      })}
                    >
                      {_.startCase(payment_status?.toLowerCase())}
                    </span>
                  </div>
                </div>
              ),
            },
            {
              name: "Offer Date",
              accessor: "offer_date",
              render: (value) => (
                <span>{moment(value?.created_at).format("DD-MM-YYYY")}</span>
              ),
            },
            {
              name: "Actions",
              accessor: "salary",
              render: ({ offer_id, offer_status }) => (
                <>
                  <ActionButton
                    action="view"
                    tooltip="View Offer"
                    onClick={dispatchAction(offer_id, "view")}
                  />
                  <Show if={offer_status === "CLOSED"}>
                    <ActionButton
                      action="expand"
                      // disabled={offer_status !== "CLOSED"}
                      tooltip="View payments"
                      onClick={() => expandItem(offer_id)}
                    />
                  </Show>
                </>
              ),
            },
          ]}
          data={offers}
          refetch={refetch}
          renderFilter={({ filterOpen, setFilterOpen }) => (
            <FilterMenu open={filterOpen} setOpen={setFilterOpen} />
          )}
          renderLoader={() => (
            <TableLoader items={[1, 1, 1, 1, 1, 1, 1, 1, 1]} />
          )}
        />
      </div>
    </div>
  );
};

export default InsurerOffers;
